import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MediaService, MEDIA_CATEGORIES } from '../../../services/media/media.service';
import { SuperAdminService } from '../../../services/super-admin/super-admin.service';
import { ToastService } from '../../../services/toast/toast.service';
import { LoaderComponent } from '../../../atoms/loader/loader.component';

// upload one image into the library. Scope options follow what the backend said
// the user may manage (data.reach). Self-saving; closes with `true` on success.
@Component({
  selector: 'app-media-upload-dialog',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatFormFieldModule, MatSelectModule, LoaderComponent],
  templateUrl: './media-upload-dialog.component.html',
  styleUrls: ['./media-upload-dialog.component.scss', '../../../../common-style/ac-dialog.scss'],
})
export class MediaUploadDialogComponent implements OnInit {
  form: FormGroup;
  file: File | null = null;
  previewUrl: string = '';
  establishments: any[] = [];
  isLoading: boolean = false;
  readonly categories = MEDIA_CATEGORIES;

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<MediaUploadDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: { reach: { canCentral: boolean; canAllEstablishments: boolean; establishmentIds: number[] } },
    private mediaService: MediaService,
    private superAdminService: SuperAdminService,
    private toastService: ToastService
  ) {
    this.form = this.fb.group({
      scope: [data.reach.canCentral ? 'CENTRAL' : 'ESTABLISHMENT', Validators.required],
      establishmentId: [null],
      category: ['UTSAV', Validators.required],
      name: ['', Validators.required],
      remarks: [''],
    });
  }

  ngOnInit(): void {
    this.loadEstablishments();
    const activeEst = Number(localStorage.getItem('activeEstablishmentId'));
    if (activeEst) this.form.patchValue({ establishmentId: activeEst });
  }

  // the establishments the user may upload for: their login list; the super admin
  // (no branch roles) takes the full list from the super-admin API.
  private loadEstablishments(): void {
    try {
      const stored = JSON.parse(localStorage.getItem('establishments') || '[]');
      this.establishments = Array.isArray(stored) ? stored : [];
    } catch {
      this.establishments = [];
    }
    if (!this.establishments.length && localStorage.getItem('isSuperAdmin') === 'true') {
      this.superAdminService.listEstablishments().subscribe({
        next: (res: any) => (this.establishments = res?.data?.establishments || []),
        error: () => {},
      });
    }
    if (!this.data.reach.canAllEstablishments && this.data.reach.establishmentIds?.length) {
      const allowed = new Set(this.data.reach.establishmentIds.map(Number));
      this.establishments = this.establishments.filter((e) => allowed.has(Number(e.establishmentId)));
    }
  }

  get showEstablishment(): boolean {
    return this.form.get('scope')?.value === 'ESTABLISHMENT';
  }

  get canPickCentral(): boolean {
    return !!this.data.reach.canCentral;
  }

  get canPickEstablishment(): boolean {
    return this.data.reach.canAllEstablishments || (this.data.reach.establishmentIds?.length ?? 0) > 0;
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    this.file = file || null;
    this.previewUrl = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => (this.previewUrl = String(reader.result || ''));
    reader.readAsDataURL(file);
    if (!this.form.get('name')?.value) {
      this.form.patchValue({ name: file.name.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ') });
    }
  }

  submit(): void {
    const value = this.form.value;
    if (!this.file) {
      this.toastService.error('Choose an image to upload.');
      return;
    }
    if (this.form.invalid || (value.scope === 'ESTABLISHMENT' && !value.establishmentId)) {
      this.form.markAllAsTouched();
      this.toastService.error(
        value.scope === 'ESTABLISHMENT' && !value.establishmentId
          ? 'Select the establishment.'
          : 'Scope, category and name are required.'
      );
      return;
    }
    this.isLoading = true;
    this.mediaService
      .uploadMedia(this.file, {
        scope: value.scope,
        establishmentId: value.scope === 'ESTABLISHMENT' ? Number(value.establishmentId) : null,
        category: value.category,
        name: value.name.trim(),
        remarks: value.remarks?.trim() || undefined,
      })
      .subscribe({
        next: (res: any) => {
          this.toastService.success(res?.message || 'Media uploaded.');
          this.isLoading = false;
          this.dialogRef.close(true);
        },
        error: (err) => {
          this.isLoading = false;
          this.toastService.error(err?.error?.message || 'Failed to upload media.');
        },
      });
  }

  close(): void {
    this.dialogRef.close();
  }
}
